import { clientConfig } from '@/config/clientConfig';
import { Can } from '@/domains/access-control';
import { useAreas, useRegions } from '@/domains/locations';
import {
  MAX_SHIFT_DATE,
  MIN_SHIFT_DATE,
  type ShiftListItem,
  type TeamShiftAssignment,
  getShiftDateError,
  getShiftDateRangeError,
} from '@/domains/shifts';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { PageHeader } from '@/shared/components/PageHeader';
import { ALLOWED_TEXT_PATTERN, TEXT_VALIDATION_MSG } from '@/shared/lib/validation';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  Clock,
  Loader2,
  Pencil,
  Search,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  NO_CAPACITY_LABEL,
  formatUtilisationDetail,
  formatUtilisationRate,
  hasCapacity,
  utilisationBarClass,
  utilisationBarWidth,
  utilisationTextClass,
} from '../lib/utilisationDisplay';
import { type UtilisationPeriod, periodDescription } from '../lib/utilisationPeriod';
import type { TeamDetail, UpdateTeamInput } from '../types/team';
import { AddTeamMembersModal } from './AddTeamMembersModal';
import { DeleteTeamDialog } from './DeleteTeamDialog';

interface TeamDetailViewProps {
  team: TeamDetail | null;
  isLoading?: boolean;
  onBack: () => void;
  allShifts?: ShiftListItem[];
  assignedShifts?: TeamShiftAssignment[];
  /** Period the `team.utilization` figure covers — drives its label (CWL-157). */
  period?: UtilisationPeriod;
  /** Resolved range, e.g. "Mon 7 – Sun 13 Sep 2026". */
  utilisationPeriodLabel?: string;
  /** The period control, rendered inside the utilisation card. */
  utilisationControl?: ReactNode;
  onAddMembers?: (userIds: number[]) => Promise<void>;
  onRemoveMember?: (userId: number, newTeamLeadId?: number) => Promise<void>;
  onUpdateTeam?: (data: UpdateTeamInput) => Promise<void>;
  onAssignShift?: (shiftId: number, startDate: string, endDate: string) => Promise<void>;
  onRemoveShift?: (shiftId: number, startDate: string, endDate: string) => Promise<void>;
  onDeleteTeam?: () => Promise<void>;
}

export function TeamDetailView({
  team,
  isLoading = false,
  onBack,
  allShifts = [],
  assignedShifts = [],
  period = 'week',
  utilisationPeriodLabel,
  utilisationControl,
  onAddMembers,
  onRemoveMember,
  onUpdateTeam,
  onAssignShift,
  onRemoveShift,
  onDeleteTeam,
}: TeamDetailViewProps) {
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  // Regions module (SC-6259): only opted-in brands get the region coverage
  // selector and the region-name suffix on areas.
  const regionsEnabled = clientConfig.features.regions;
  // Area coverage is selected from a searchable, paginated list (the area
  // table is large); regions cover the bulk case. Fetch the first page here.
  const [areaSearch, setAreaSearch] = useState('');
  const { data: areasData } = useAreas({ search: areaSearch || undefined, limit: 50 });
  const allAreas = areasData?.items ?? [];
  const { data: allRegions = [] } = useRegions({ enabled: regionsEnabled });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignShiftModal, setShowAssignShiftModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editForm, setEditForm] = useState<UpdateTeamInput>({});
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [shiftStartDate, setShiftStartDate] = useState('');
  const [shiftEndDate, setShiftEndDate] = useState('');
  const [shiftSearchTerm, setShiftSearchTerm] = useState('');
  const [isAssignShiftSubmitting, setIsAssignShiftSubmitting] = useState(false);
  const [removingShiftKey, setRemovingShiftKey] = useState<string | null>(null);
  // Assigned shift pending unassign — drives the confirmation dialog (null = closed).
  const [shiftToRemove, setShiftToRemove] = useState<TeamShiftAssignment | null>(null);
  // Member pending removal — drives the confirmation dialog (null = closed).
  const [memberToRemove, setMemberToRemove] = useState<{ userId: number; name: string } | null>(
    null,
  );
  // When removing the current team lead, prompt for a replacement before removing.
  const [leadReassign, setLeadReassign] = useState<{ leadUserId: number } | null>(null);
  const [newLeadId, setNewLeadId] = useState<number | null>(null);
  const [isReassignSubmitting, setIsReassignSubmitting] = useState(false);

  const existingUserIds = new Set((team?.members ?? []).map((m) => String(m.userId)));

  const handleDeleteTeam = async () => {
    setIsDeleting(true);
    try {
      await onDeleteTeam?.();
      setShowDeleteDialog(false);
    } catch {
      // Success/error toasts handled globally via the mutation meta.
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveMemberClick = (member: { userId: number; name: string }) => {
    // Removing the current team lead requires choosing a replacement first, so
    // it uses its own reassign modal rather than the plain confirm dialog.
    if (team?.teamLeadId === member.userId) {
      const others = (team?.members ?? []).filter((m) => m.userId !== member.userId);
      if (others.length === 0) {
        showErrorToast(
          'Cannot Remove Team Leader',
          'Add another member before removing the last team leader.',
        );
        return;
      }
      setNewLeadId(null);
      setLeadReassign({ leadUserId: member.userId });
      return;
    }

    setMemberToRemove(member);
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return;
    try {
      await onRemoveMember?.(memberToRemove.userId);
      setMemberToRemove(null);
    } catch {
      // Success/error toasts handled globally via the mutation meta.
    }
  };

  const handleConfirmReassignAndRemove = async () => {
    if (!leadReassign || !newLeadId) return;
    setIsReassignSubmitting(true);
    try {
      await onRemoveMember?.(leadReassign.leadUserId, newLeadId);
      setLeadReassign(null);
      setNewLeadId(null);
    } catch {
      // Success/error toasts handled globally via the mutation meta.
    } finally {
      setIsReassignSubmitting(false);
    }
  };

  const openEditModal = () => {
    setEditForm({
      name: team?.name ?? '',
      description: team?.description ?? undefined,
      maxCapacity: team?.maxCapacity ?? undefined,
      teamLeadId: team?.teamLeadId ?? null,
      areaIds: team?.areaIds ?? [],
      regionIds: team?.regionIds ?? [],
    });
    setShowEditModal(true);
  };

  const validateEditForm = (): boolean => {
    const errors: Record<string, string> = {};
    const name = editForm.name?.trim() ?? '';
    const description = editForm.description?.trim() ?? '';

    if (!name) {
      errors.name = 'Team name is required';
    } else if (name.length > 50) {
      errors.name = 'Team name must be 50 characters or less';
    } else if (!ALLOWED_TEXT_PATTERN.test(name)) {
      errors.name = TEXT_VALIDATION_MSG;
    }

    if (description && description.length > 50) {
      errors.description = 'Description must be 50 characters or less';
    } else if (description && !ALLOWED_TEXT_PATTERN.test(description)) {
      errors.description = TEXT_VALIDATION_MSG;
    }

    if (editForm.maxCapacity != null) {
      const cap = Number(editForm.maxCapacity);
      if (Number.isNaN(cap) || cap < 1 || cap > 50) {
        errors.capacity = 'Daily job capacity must be between 1 and 50';
      }
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateEditForm()) return;
    setIsEditSubmitting(true);
    try {
      await onUpdateTeam?.(editForm);
      setShowEditModal(false);
    } catch {
      // Success/error toasts handled globally via the mutation meta.
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const filteredShifts = allShifts.filter((s) =>
    s.title.toLowerCase().includes(shiftSearchTerm.toLowerCase()),
  );

  const closeAssignShiftModal = () => {
    setShowAssignShiftModal(false);
    setSelectedShiftId(null);
    setShiftStartDate('');
    setShiftEndDate('');
    setShiftSearchTerm('');
  };

  // Chrome's date input accepts a year far outside the supported range and reports
  // it verbatim, so guard the values rather than trusting the picker (SC-6530).
  // Each field is also checked alone so a bad start date is flagged before the end
  // date has been filled in.
  const shiftStartDateError = getShiftDateError(shiftStartDate);
  const shiftEndDateError = getShiftDateError(shiftEndDate);
  const shiftDateError = getShiftDateRangeError(shiftStartDate, shiftEndDate);
  // An out-of-order range implicates both inputs; a per-field error only its own.
  const isShiftOrderingError =
    shiftDateError !== null && !shiftStartDateError && !shiftEndDateError;

  const handleAssignShiftSubmit = async () => {
    if (!selectedShiftId || !shiftStartDate || !shiftEndDate || shiftDateError) return;
    setIsAssignShiftSubmitting(true);
    try {
      // The assign hook owns the success toast (success / partial / fully-conflicted),
      // since only it knows the per-member summary. The error toast is handled
      // globally via the mutation meta. Don't announce either here.
      await onAssignShift?.(selectedShiftId, shiftStartDate, shiftEndDate);
      closeAssignShiftModal();
    } catch {
      // Error toast handled globally via the mutation meta.
    } finally {
      setIsAssignShiftSubmitting(false);
    }
  };

  const handleConfirmRemoveShift = async () => {
    if (!shiftToRemove) return;
    const shift = shiftToRemove;
    const key = `${shift.shiftId}-${shift.startDate}-${shift.endDate}`;
    setRemovingShiftKey(key);
    try {
      await onRemoveShift?.(shift.shiftId, shift.startDate, shift.endDate);
      setShiftToRemove(null);
    } catch {
      // Success/error toasts handled globally via the mutation meta.
    } finally {
      setRemovingShiftKey(null);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Team not found</p>
          <Button variant="outline" className="mt-4" onClick={onBack}>
            Back to Teams
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden">
        <PageHeader
          title={team.name}
          subtitle={team.description ?? ''}
          icon={<UsersIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
          actions={
            <div className="flex gap-2">
              <Can action="update" subject="Team">
                <Button variant="outline" onClick={openEditModal}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Team
                </Button>
              </Can>
              <Can action="update" subject="Team">
                <Button variant="outline" onClick={() => setShowAssignShiftModal(true)}>
                  <Clock className="w-4 h-4 mr-2" />
                  Assign Shift
                </Button>
              </Can>
              <Can action="create" subject="Team">
                <Button
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={() => setShowAddMemberModal(true)}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Members
                </Button>
              </Can>
              {onDeleteTeam && (
                <Can action="delete" subject="Team">
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Team
                  </Button>
                </Can>
              )}
            </div>
          }
        />

        <div className="flex-1 overflow-y-auto">
          {/* Team Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Team Members</div>
              <div className="text-2xl font-bold text-foreground">{team.memberCount}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Daily Job Capacity</div>
              <div className="text-2xl font-bold text-foreground">{team.maxCapacity ?? '—'}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Active Jobs</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {team.activeJobs}
              </div>
            </div>
          </div>

          {/*
            Utilisation gets its own card rather than a slot in the stat grid: the
            figure is only meaningful alongside its period and the hours it came
            from, which is what CWL-157 was raised about.
          */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm text-muted-foreground mb-1">
                  Utilisation · {periodDescription(period)}
                </div>
                <div
                  className={`text-2xl font-bold ${utilisationTextClass(team.utilization)}`}
                  data-testid="team-utilisation-rate"
                >
                  {formatUtilisationRate(team.utilization)}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {formatUtilisationDetail(team.utilization)}
                </div>
                {utilisationPeriodLabel && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {utilisationPeriodLabel}
                  </div>
                )}
                {!hasCapacity(team.utilization) && (
                  <div className="text-xs text-muted-foreground mt-1">{NO_CAPACITY_LABEL}</div>
                )}
                {team.utilization.rate > 100 && (
                  <div className="text-xs font-medium text-red-600 dark:text-red-400 mt-1">
                    Over capacity
                  </div>
                )}
              </div>
              {utilisationControl}
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-3">
              <div
                className={`h-full transition-all ${utilisationBarClass(team.utilization)}`}
                style={{ width: utilisationBarWidth(team.utilization) }}
              />
            </div>
          </div>

          {/* Assigned Shifts List */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Assigned Shifts</h3>
              <Badge
                variant="outline"
                className="bg-orange-50 dark:bg-orange-950/30 text-orange-600 border-orange-200 dark:border-orange-800"
              >
                {assignedShifts.length} {assignedShifts.length === 1 ? 'shift' : 'shifts'}
              </Badge>
            </div>

            <div className="space-y-3">
              {assignedShifts.map((shift) => {
                const key = `${shift.shiftId}-${shift.startDate}-${shift.endDate}`;
                const isRemoving = removingShiftKey === key;
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className="w-3 h-10 rounded flex-shrink-0"
                        style={{ backgroundColor: shift.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground">
                          {shift.title}
                          {shift.code ? (
                            <span className="text-muted-foreground font-normal">
                              {' '}
                              ({shift.code})
                            </span>
                          ) : null}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {shift.startTime} - {shift.endTime}
                        </div>
                      </div>
                      <div className="hidden sm:block text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(shift.startDate)} – {formatDate(shift.endDate)}
                      </div>
                      <Badge variant="outline" className="text-xs whitespace-nowrap">
                        {shift.memberCount} {shift.memberCount === 1 ? 'member' : 'members'}
                      </Badge>
                    </div>
                    <Can action="delete" subject="Shift">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={() => setShiftToRemove(shift)}
                        disabled={isRemoving}
                        aria-label={`Remove ${shift.title} shift`}
                      >
                        {isRemoving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </Can>
                  </div>
                );
              })}

              {assignedShifts.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No shifts assigned to this team yet</p>
                  <Can action="update" subject="Team">
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setShowAssignShiftModal(true)}
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Assign First Shift
                    </Button>
                  </Can>
                </div>
              )}
            </div>
          </div>

          {/* Team Members List */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Team Members</h3>
              <Badge
                variant="outline"
                className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 border-blue-200 dark:border-blue-800"
              >
                {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
              </Badge>
            </div>

            <div className="space-y-3">
              {team.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <Avatar className="w-10 h-10 bg-orange-600 text-white">
                      <AvatarFallback className="bg-orange-600 text-white text-sm">
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground flex items-center gap-2">
                        {member.name}
                        {team.teamLeadId === member.userId && (
                          <Badge className="bg-orange-600 text-white border-0 text-xs">Lead</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{member.role ?? 'Member'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          member.taskId !== null
                            ? 'bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30'
                            : 'bg-slate-50 text-slate-700 border-slate-400 dark:bg-slate-900/30'
                        }
                      >
                        {member.taskId !== null ? 'active' : 'available'}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() =>
                      handleRemoveMemberClick({ userId: member.userId, name: member.name })
                    }
                    aria-label={`Remove ${member.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {team.members.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No members in this team yet</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowAddMemberModal(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add First Member
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Team Modal */}
      <Dialog
        open={showEditModal}
        onOpenChange={(next) => {
          if (!next) setShowEditModal(false);
        }}
      >
        <DialogContent
          className="max-w-md max-h-[90vh] flex flex-col p-0"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="p-4 border-b border-border shrink-0">
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>Update team details</DialogDescription>
          </DialogHeader>

          <div className="p-4 space-y-4 overflow-y-auto">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Team Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={editForm.name ?? ''}
                onChange={(e) => {
                  setEditForm((f) => ({ ...f, name: e.target.value }));
                  if (editErrors.name) setEditErrors((prev) => ({ ...prev, name: '' }));
                }}
                maxLength={50}
                placeholder="Enter team name"
              />
              {editErrors.name && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editErrors.name}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Description
              </label>
              <Input
                value={editForm.description ?? ''}
                onChange={(e) => {
                  setEditForm((f) => ({ ...f, description: e.target.value || undefined }));
                  if (editErrors.description)
                    setEditErrors((prev) => ({ ...prev, description: '' }));
                }}
                maxLength={50}
                placeholder="Optional description"
              />
              {editErrors.description && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {editErrors.description}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Daily Job Capacity
              </label>
              <Input
                type="number"
                min={1}
                max={50}
                value={editForm.maxCapacity ?? ''}
                onChange={(e) => {
                  setEditForm((f) => ({
                    ...f,
                    maxCapacity: e.target.value ? Number(e.target.value) : undefined,
                  }));
                  if (editErrors.capacity) setEditErrors((prev) => ({ ...prev, capacity: '' }));
                }}
                placeholder="Max jobs per day (1–50)"
              />
              {editErrors.capacity && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editErrors.capacity}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Team Lead</label>
              <Select
                value={
                  editForm.teamLeadId !== undefined && editForm.teamLeadId !== null
                    ? String(editForm.teamLeadId)
                    : '__none__'
                }
                onValueChange={(value) =>
                  setEditForm((f) => ({
                    ...f,
                    teamLeadId: value === '__none__' ? null : Number(value),
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="No team lead" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No team lead</SelectItem>
                  {team.members.map((m) => (
                    <SelectItem key={m.id} value={String(m.userId)}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Choose a lead from the current members
              </p>
            </div>

            {regionsEnabled && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Coverage — Regions
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  Cover entire regions. A job in any of a region's areas can be offered to this
                  team.
                </p>
                <div className="max-h-32 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                  {allRegions.length === 0 && (
                    <p className="text-xs text-muted-foreground">No regions defined.</p>
                  )}
                  {allRegions.map((r) => {
                    const checked = (editForm.regionIds ?? []).includes(r.id);
                    return (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setEditForm((f) => {
                              const set = new Set(f.regionIds ?? []);
                              if (e.target.checked) set.add(r.id);
                              else set.delete(r.id);
                              return { ...f, regionIds: Array.from(set) };
                            })
                          }
                        />
                        <span className="text-foreground">{r.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Coverage — Areas
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                Cover specific areas by postcode. Search to find areas.
              </p>
              <Input
                value={areaSearch}
                onChange={(e) => setAreaSearch(e.target.value)}
                placeholder="Search areas..."
                className="mb-2 h-8 text-sm"
              />
              <div className="max-h-32 overflow-y-auto border border-border rounded-md p-2 space-y-1">
                {allAreas.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    {areaSearch ? 'No areas match.' : 'No areas defined.'}
                  </p>
                )}
                {allAreas.map(
                  (a: { id: number; name: string | null; region_name: string | null }) => {
                    const checked = (editForm.areaIds ?? []).includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setEditForm((f) => {
                              const set = new Set(f.areaIds ?? []);
                              if (e.target.checked) set.add(a.id);
                              else set.delete(a.id);
                              return { ...f, areaIds: Array.from(set) };
                            })
                          }
                        />
                        <span className="text-foreground">
                          {a.name}
                          {regionsEnabled && a.region_name ? ` · ${a.region_name}` : ''}
                        </span>
                      </label>
                    );
                  },
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 p-4 border-t border-border shrink-0">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={handleEditSubmit}
              disabled={!editForm.name?.trim() || isEditSubmitting}
            >
              {isEditSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reassign Team Leader Modal — shown when removing the current lead */}
      {leadReassign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Select New Team Leader</h2>
                <p className="text-sm text-muted-foreground">
                  This member is the team leader. Choose a replacement before removing them.
                </p>
              </div>
              <button
                onClick={() => {
                  setLeadReassign(null);
                  setNewLeadId(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
              {team.members
                .filter((m) => m.userId !== leadReassign.leadUserId)
                .map((m) => (
                  <div
                    key={m.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      newLeadId === m.userId
                        ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/20'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => setNewLeadId(m.userId)}
                  >
                    <Avatar className="w-9 h-9 bg-blue-600 text-white">
                      <AvatarFallback className="bg-blue-600 text-white text-sm">
                        {getInitials(m.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground text-sm">{m.name}</div>
                      <div className="text-xs text-muted-foreground">{m.role ?? 'Member'}</div>
                    </div>
                    {newLeadId === m.userId && (
                      <Badge className="bg-orange-600 text-white border-0 text-xs">
                        New Leader
                      </Badge>
                    )}
                  </div>
                ))}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setLeadReassign(null);
                  setNewLeadId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleConfirmReassignAndRemove}
                disabled={!newLeadId || isReassignSubmitting}
              >
                {isReassignSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reassign &amp; Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      <AddTeamMembersModal
        open={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        existingMemberIds={existingUserIds}
        onAddMembers={async (userIds) => {
          await onAddMembers?.(userIds);
        }}
      />

      {/* Assign Shift Modal */}
      <Dialog
        open={showAssignShiftModal}
        onOpenChange={(next) => {
          if (!next) closeAssignShiftModal();
        }}
      >
        <DialogContent
          className="max-w-lg max-h-[80vh] flex flex-col p-0"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle>Assign Shift to Team</DialogTitle>
            <DialogDescription>Select a shift and date range for {team?.name}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Select Shift *
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search shifts..."
                  value={shiftSearchTerm}
                  onChange={(e) => setShiftSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                {filteredShifts.map((shift) => (
                  <div
                    key={shift.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-all ${
                      selectedShiftId === shift.id
                        ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-600'
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => setSelectedShiftId(shift.id)}
                  >
                    <div
                      className="w-4 h-4 rounded flex-shrink-0"
                      style={{ backgroundColor: shift.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-foreground">{shift.title}</div>
                      <div className="text-xs text-muted-foreground">
                        {shift.startTime} - {shift.endTime}
                        {shift.code ? ` (${shift.code})` : ''}
                      </div>
                    </div>
                    {selectedShiftId === shift.id && (
                      <Badge className="bg-orange-600 text-white border-0 text-xs">Selected</Badge>
                    )}
                  </div>
                ))}
                {filteredShifts.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No shifts found
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Start Date *
                </label>
                <Input
                  type="date"
                  value={shiftStartDate}
                  min={MIN_SHIFT_DATE}
                  max={MAX_SHIFT_DATE}
                  onChange={(e) => setShiftStartDate(e.target.value)}
                  aria-invalid={shiftStartDateError !== null || isShiftOrderingError}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  End Date *
                </label>
                <Input
                  type="date"
                  value={shiftEndDate}
                  min={MIN_SHIFT_DATE}
                  max={MAX_SHIFT_DATE}
                  onChange={(e) => setShiftEndDate(e.target.value)}
                  aria-invalid={shiftEndDateError !== null || isShiftOrderingError}
                />
              </div>
            </div>
            {shiftDateError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{shiftDateError}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 p-4 border-t border-border">
            <Button variant="outline" onClick={closeAssignShiftModal}>
              Cancel
            </Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white"
              onClick={handleAssignShiftSubmit}
              disabled={
                !selectedShiftId ||
                !shiftStartDate ||
                !shiftEndDate ||
                shiftDateError !== null ||
                isAssignShiftSubmitting
              }
            >
              {isAssignShiftSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign Shift
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteTeamDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        teamName={team?.name}
        memberCount={team?.members?.length}
        isDeleting={isDeleting}
        onConfirm={handleDeleteTeam}
      />

      <ConfirmDialog
        open={shiftToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setShiftToRemove(null);
        }}
        title="Unassign Shift"
        description={
          shiftToRemove ? `"${shiftToRemove.title}" will be unassigned from this team.` : undefined
        }
        confirmLabel="Unassign"
        pendingLabel="Removing…"
        isPending={removingShiftKey !== null}
        onConfirm={handleConfirmRemoveShift}
      />

      <ConfirmDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null);
        }}
        title="Remove Team Member"
        description={
          memberToRemove ? `${memberToRemove.name} will be removed from this team.` : undefined
        }
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={handleConfirmRemoveMember}
      />
    </>
  );
}
